// The typed client for /v1 (types generated from the API's OpenAPI document, src/gen/api.ts).
// The console authenticates with the session cookie only: same origin locally (the Vite proxy),
// and `credentials: 'include'` against VITE_API_URL in production, where the API allows the
// console's origin (apps/api/src/v1.ts).
import createClient from 'openapi-fetch'
import type { paths } from '../gen/api'
import { sandboxHeader } from '../sandbox'

export const API_BASE = `${(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')}/v1`

export const api = createClient<paths>({
  baseUrl: API_BASE,
  credentials: 'include',
  // Looked up on each call, so tests (and polyfills) can replace it.
  fetch: (request: Request) => globalThis.fetch(request),
})

// The sandbox's reads carry its token (sandbox.ts); nothing else ever does.
api.use({
  onRequest({ request }) {
    const header = sandboxHeader(request.url, request.method)
    if (header) request.headers.set('Authorization', header)
    return request
  },
})

/** An error answer from the API, with its code (`unauthorized`, `forbidden`, `not_found`...). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** With `erasing` (410): what is being deleted (APS-406). */
    readonly subject?: 'organization' | 'project',
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function errorBody(body: unknown): {
  code: string
  message: string
  subject?: 'organization' | 'project'
} {
  const error = (
    body as { error?: { code?: unknown; message?: unknown; subject?: unknown } } | undefined
  )?.error
  const subject = error?.subject
  return {
    code: typeof error?.code === 'string' ? error.code : 'unknown',
    message: typeof error?.message === 'string' ? error.message : '',
    ...(subject === 'organization' || subject === 'project' ? { subject } : {}),
  }
}

/** The data of an openapi-fetch result, or an ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok && result.error === undefined) return result.data as T
  const { code, message, subject } = errorBody(result.error)
  throw new ApiError(result.response.status, code, message || result.response.statusText, subject)
}

/** A call to a route with a response schema: its data, typed from the OpenAPI document. */
export async function typed<T>(
  result: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  return unwrap(await result)
}

/** A call answered with no body (204): resolves when it succeeded, else throws an ApiError. */
export async function noContent(
  result: Promise<{ error?: unknown; response: Response }>,
): Promise<void> {
  const r = await result
  if (r.response.ok && r.error === undefined) return
  unwrap(r)
}

export const isApiError = (error: unknown, status?: number): error is ApiError =>
  error instanceof ApiError && (status === undefined || error.status === status)

/**
 * The project or its organization is being deleted (APS-406): 410 with the code erasing. Not a
 * failure, and never retried.
 */
export const erasing = (error: unknown): error is ApiError =>
  isApiError(error, 410) && error.code === 'erasing'

/** What a 410 erasing says is being deleted: the organization, else the project. */
export const erasingSubject = (error: ApiError): 'organization' | 'project' =>
  error.subject === 'organization' ? 'organization' : 'project'

/**
 * A feature this server runs without (alerts without ALERTS_KEY): 503 with the code
 * not_configured (APS-381). A plain 503 is an outage, and stays an error.
 */
export const notConfigured = (error: unknown): boolean =>
  isApiError(error, 503) && error.code === 'not_configured'
